import { Option } from '@spike/model';
import { OrganizationMarketplace } from '@spike/marketplace-model';
import {
    isServiceCategoryEnabled,
    OrganizationServiceCategory
} from 'hooks/useOrganizationMarketplacesByServiceCategory';
import { getServiceCategoryByBusinessArea } from '../tabs';
import { isSettingEnabled } from 'utils/SettingsUtils';

export const EXPORT_SERVICE_CATEGORY_IDS: Array<OrganizationServiceCategory> = [
    'grooming',
    'evaluation',
    'daycare',
    'boarding',
    'training',
    'transportation',
    'walking'
];

export const EXPORT_SERVICE_CATEGORY_LABELS: Record<OrganizationServiceCategory, string> = {
    grooming: 'Grooming',
    evaluation: 'Evaluation',
    daycare: 'Daycare',
    boarding: 'Boarding',
    training: 'Training',
    transportation: 'Transportation',
    walking: 'Walking'
};

export interface MarketplaceServiceCategoryFlags {
    hasGrooming: boolean;
    hasMobileGrooming: boolean;
    hasDental: boolean;
    hasDaycare: boolean;
    hasBoarding: boolean;
    hasTransportation: boolean;
    hasTraining: boolean;
    hasEvaluation: boolean;
    hasWalking: boolean;
}

const isMobileGroomingEnabled = (marketplace: OrganizationMarketplace): boolean =>
    isSettingEnabled(marketplace.settings, 'mobile_grooming_enabled');

export const getGroomingExportLabel = (hasGrooming: boolean, hasDental: boolean): string =>
    `${hasGrooming ? 'Grooming' : ''}${hasGrooming && hasDental ? ' / ' : ''}${hasDental ? 'Dental' : ''}` ||
    EXPORT_SERVICE_CATEGORY_LABELS.grooming;

export const getEnabledExportCategoryIdsFromFlags = (
    flags: MarketplaceServiceCategoryFlags
): Set<OrganizationServiceCategory> => {
    const ids = new Set<OrganizationServiceCategory>();

    if (flags.hasGrooming || flags.hasMobileGrooming || flags.hasDental) {
        ids.add('grooming');
    }
    if (flags.hasEvaluation) {
        ids.add('evaluation');
    }
    if (flags.hasDaycare) {
        ids.add('daycare');
    }
    if (flags.hasBoarding) {
        ids.add('boarding');
    }
    if (flags.hasTraining) {
        ids.add('training');
    }
    if (flags.hasTransportation) {
        ids.add('transportation');
    }
    if (flags.hasWalking) {
        ids.add('walking');
    }

    return ids;
};

export const getEnabledExportCategoryIdsFromMarketplace = (
    marketplace: OrganizationMarketplace
): Set<OrganizationServiceCategory> => {
    const ids = new Set<OrganizationServiceCategory>();

    EXPORT_SERVICE_CATEGORY_IDS.forEach(category => {
        if (category === 'grooming') {
            if (isServiceCategoryEnabled(marketplace, 'grooming') || isMobileGroomingEnabled(marketplace)) {
                ids.add('grooming');
            }
            return;
        }

        if (isServiceCategoryEnabled(marketplace, category)) {
            ids.add(category);
        }
    });

    return ids;
};

export const getExportServiceCategoryOptions = (
    organizationMarketplaces: Array<OrganizationMarketplace>,
    selectedMarketplaceIds: Array<number>,
    currentMarketplaceId: number | null | undefined,
    currentFlags: MarketplaceServiceCategoryFlags
): Array<Option<string>> => {
    const scopedMarketplaces =
        selectedMarketplaceIds.length === 0
            ? organizationMarketplaces
            : organizationMarketplaces.filter(marketplace => selectedMarketplaceIds.includes(marketplace.id));

    const enabledIds = new Set<OrganizationServiceCategory>();

    scopedMarketplaces.forEach(marketplace => {
        getEnabledExportCategoryIdsFromMarketplace(marketplace).forEach(id => enabledIds.add(id));
    });

    const includeCurrentFlags =
        organizationMarketplaces.length === 0 ||
        selectedMarketplaceIds.length === 0 ||
        (currentMarketplaceId != null && selectedMarketplaceIds.includes(currentMarketplaceId));

    if (includeCurrentFlags) {
        getEnabledExportCategoryIdsFromFlags(currentFlags).forEach(id => enabledIds.add(id));
    }

    const hasGrooming =
        scopedMarketplaces.some(
            marketplace => isServiceCategoryEnabled(marketplace, 'grooming') || isMobileGroomingEnabled(marketplace)
        ) ||
        (includeCurrentFlags && (currentFlags.hasGrooming || currentFlags.hasMobileGrooming));
    const hasDental = includeCurrentFlags && currentFlags.hasDental;

    return EXPORT_SERVICE_CATEGORY_IDS.filter(id => enabledIds.has(id)).map(id => ({
        id,
        name: id === 'grooming' ? getGroomingExportLabel(hasGrooming, hasDental) : EXPORT_SERVICE_CATEGORY_LABELS[id]
    }));
};

export const appointmentMatchesSelectedExportCategories = (
    businessArea: string | null | undefined,
    selectedCategories: Array<string>
): boolean => {
    const serviceCategory = getServiceCategoryByBusinessArea(businessArea);
    return serviceCategory !== 'all' && selectedCategories.includes(serviceCategory);
};
